import React, { useState, useEffect } from 'react';
import './CoursesAssessment.css';
import AssuranceReport from './AssuranceReport';
import { getAllCourses } from '../services/api';
import { useLocation, useNavigate } from 'react-router-dom';

const CoursesAssessment = ({ onBack, sidebarExpanded }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentSchool, setCurrentSchool] = useState('MARYLAND');
  
  // Get school from URL parameters on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    setCurrentSchool(school);
  }, []);

  // Log when component mounts to verify correct onBack prop
  useEffect(() => {
    //console.log("CoursesAssessment mounted with onBack:", !!onBack);
  }, [onBack]);
  
  // Gradient colors for cards
  const gradients = [
    'linear-gradient(135deg, #f5f7ff 0%, #e0e6ff 100%)',
    'linear-gradient(135deg, #fff5f5 0%, #ffe0e0 100%)',
    'linear-gradient(135deg, #f5fff7 0%, #e0ffe6 100%)',
    'linear-gradient(135deg, #f5f5ff 0%, #e5e0ff 100%)',
    'linear-gradient(135deg, #fffbf5 0%, #fff5e0 100%)',
    'linear-gradient(135deg, #f8f5ff 0%, #eee0ff 100%)'
  ];
  
  // Check if we're returning from evidence page with state
  useEffect(() => {
    if (location.state?.showAssuranceReport && location.state?.courseList) {
      //console.log("Returning from evidence page with state:", location.state);
      
      // Get school from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const school = urlParams.get('school') || 'MARYLAND';
      
      // Set the selected course from the state
      setSelectedCourse({
        name: location.state.programName || location.state.courseName,
        courseList: location.state.courseList,
        school: school,
        returnedFromEvidence: true,
        fullAlignmentData: location.state.fullAlignmentData,
        originalTableData: location.state.originalTableData,
        originalData: location.state.originalData
      });
    }
  }, [location.state]);
  
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        // Get school from URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const school = urlParams.get('school') || 'MARYLAND'; // Default to MARYLAND if not found
        
        const data = await getAllCourses(school, location.state?.programName);
        // Transform the course list into the required format
        const formattedCourses = data.course_list.map(courseName => ({
          id: courseName,
          name: courseName,
          score: 0, // Default score
          category: 'Business', // Default category
          school: school // Store the school with each course
        }));
        setCourses(formattedCourses);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCourses();
  }, []);

  const filteredCourses = courses.filter(course => {
    const matchesFilter = filter === 'All' || course.category === filter;
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleStartAssessment = (course) => {
    // Log the selected course for debugging
    //console.log("Selected course:", course);
    
    // Get school from URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND'; // Default to MARYLAND if not found
    
    // Add/update school parameter to URL
    const url = new URL(window.location.href);
    url.searchParams.set('school', school);
    window.history.pushState({}, '', url);
    
    // Create a deep copy of the course to avoid reference issues
    const selectedCourse = {
      ...course,
      courseList: [course.name],
      school: school
    };
    
    // Set the selected course state with the correct course data
    setSelectedCourse(selectedCourse);
  };

  const handleBackFromReport = () => {
    setSelectedCourse(null);
  };
  
  const handleBackClick = () => {
    // Get the school parameter from the URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const school = urlParams.get('school') || 'MARYLAND';
    
    // Navigate back to dashboard with school parameter
    navigate(`/dashboard?school=${school}`, {
      replace: true // Use replace to avoid adding to history stack
    });
  };

  if (selectedCourse) {
    return <AssuranceReport 
      onBack={handleBackFromReport} 
      programName={selectedCourse.name} 
      courseList={selectedCourse.courseList}
      school={selectedCourse.school}
      sidebarExpanded={sidebarExpanded}
      isCourse={true}
      returnedFromEvidence={selectedCourse.returnedFromEvidence}
      fullAlignmentData={selectedCourse.fullAlignmentData}
      originalTableData={selectedCourse.originalTableData}
      originalData={selectedCourse.originalData}
      initialTab={selectedCourse.returnedFromEvidence ? 'course-alignment-overview' : undefined}
    />;
  }

  if (isLoading) {
    return (
      <div className="courses-assessment">
        <div className="loading-container">
          <p>Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="courses-assessment">
        <div className="error-container">
          <p>Error loading courses: {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="courses-assessment">
      <img src="/page-1-right-to-courses.svg" alt="Courses" className="course-icon" />
      
      <div className="fixed-content">
        <div className="courses-header">
          <div className="filters">
            <div className="filter">
              <img src="/school.svg" alt="School" className="filter-icon" />
              <select className="filter-select" value={currentSchool} readOnly>
                <option value={currentSchool}>{currentSchool}</option>
              </select>
            </div>
            
            <div className="filter">
              <img src="/page-1-calendar.svg" alt="Calendar" className="filter-icon" />
              <select className="filter-select">
                <option>2025 Spring</option>
              </select>
            </div>
          </div>
          
          <button className="back-button-1" onClick={handleBackClick}>
            <span className="back-icon">←</span>
            Back
          </button>
        </div>

        <div className="courses-title">
          <h1>Start Mapping By Courses</h1>
        </div>
        
        <div className="search-bar">
          <input 
            type="text" 
            placeholder="Search courses..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="course-filters">
          <button 
            className={`courses-filter-button ${filter === 'All' ? 'active' : ''}`}
            onClick={() => setFilter('All')}
          >
            All
          </button>
          <button 
            className={`courses-filter-button ${filter === 'Business' ? 'active' : ''}`}
            onClick={() => setFilter('Business')}
          >
            Business
          </button>
        </div>
      </div>
      
      <div className="course-grid-container">
        <div className="course-grid">
          {filteredCourses.map((course, index) => (
            <div 
              className="course-card" 
              key={`course-${course.id}-${index}`}
              style={{ 
                background: gradients[index % gradients.length],
                height: '249px'
              }}
            >
              <div className="course-card-content">
                <h3>{course.name}</h3>
                <button 
                  className="courses-assessment-button"
                  onClick={() => handleStartAssessment(course)}
                  data-course-index={index}
                >
                  Start<span className="arrow">→</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoursesAssessment; 